// utilities/validation/eventComparisonService.js
// Service for comparing actual event data with flow expectations

/**
 * Utility function to get nested value from object using dot notation
 * @param {Object} obj - The object to traverse
 * @param {string} path - The dot notation path (e.g., "data.custom_attributes.screen")
 * @returns {*} The value at the path, or undefined if not found
 */
export const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => {
    // Handle array notation like products[0].id
    if (key.includes('[') && key.includes(']')) {
      const arrayKey = key.substring(0, key.indexOf('['));
      const index = parseInt(key.substring(key.indexOf('[') + 1, key.indexOf(']')));
      return current && current[arrayKey] && current[arrayKey][index];
    }
    return current && current[key];
  }, obj);
};

/**
 * Compare actual event data with flow expectations
 * @param {Object} actualEvent - The actual event data from API or mock
 * @param {Object} flowEvent - The expected flow event configuration
 * @param {Object} [flattenedProxy] - Optional flattened proxy data (from event_object_proxy)
 * @returns {Array} Array of comparison results with status, expected/actual values
 */
export const compareEventWithFlow = (actualEvent, flowEvent, flattenedProxy) => {
  const results = [];
  const expectedAttributes = flowEvent.scenario_attributes || [];

  // Create a flat representation of the actual event data
  const flattenedActual = {};

  // Define internal metadata fields (never display in tables)
  const INTERNAL_METADATA = [
    'event_name',
    'event_direction',
    'timestamp',
    'device_id',
    'is_blocked_event',
    'data'  // We handle this separately below
  ];

  // Flatten the data object with "data." prefix
  const flattenData = (obj, prefix = 'data') => {
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (value === null) {
        // Explicitly handle null values
        flattenedActual[fullKey] = null;
      } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        flattenData(value, fullKey);
      } else if (Array.isArray(value)) {
        // Handle arrays - flatten each item with index
        value.forEach((item, index) => {
          if (item === null) {
            flattenedActual[`${fullKey}[${index}]`] = null;
          } else if (item && typeof item === 'object') {
            flattenData(item, `${fullKey}[${index}]`);
          } else {
            flattenedActual[`${fullKey}[${index}]`] = item;
          }
        });
      } else {
        flattenedActual[fullKey] = value;
      }
    });
  };

  // Flatten nested data object with "data." prefix
  if (actualEvent.data) {
    flattenData(actualEvent.data);
  }

  // Add top-level event_object properties WITHOUT prefix (e.g., event_type from mParticle)
  // Exclude internal metadata
  Object.keys(actualEvent).forEach(key => {
    if (!INTERNAL_METADATA.includes(key)) {
      flattenedActual[key] = actualEvent[key];
    }
  });


  // Check each expected attribute
  expectedAttributes.forEach(expectedAttr => {
    const attributeName = expectedAttr.attribute_name;
    const expectedValue = expectedAttr.expected_value;
    const attributeType = expectedAttr.attribute_type;

    const actualValue = flattenedActual[attributeName];

    let status = "Pass";
    let displayActualValue = "";

    if (actualValue === undefined) {
      status = "Missing";
      displayActualValue = "";
    } else if (actualValue === null) {
      // Show null values explicitly
      displayActualValue = "null";
      status = attributeType !== "Dynamic" && expectedValue !== "null" ? "Fail" : "Pass";
    } else if (actualValue === "") {
      displayActualValue = "";
      status = "Fail";
    } else if (attributeType !== "Dynamic") {
      // For Static attributes, do exact comparison with proper type handling
      displayActualValue = actualValue === null ? "null" : String(actualValue);

      // Debug logging to see what's being compared

      // Handle different comparison scenarios
      if (expectedValue === "null" && actualValue === null) {
        status = "Pass";
      }
      // If expected is "false" string and actual is false boolean, they should match
      else if (expectedValue === "false" && actualValue === false) {
        status = "Pass";
      }
      // If expected is "true" string and actual is true boolean, they should match
      else if (expectedValue === "true" && actualValue === true) {
        status = "Pass";
      }
      // For numbers, convert both to strings for comparison
      else if (typeof actualValue === 'number' && !isNaN(Number(expectedValue))) {
        status = String(actualValue) === String(expectedValue) ? "Pass" : "Fail";
      }
      // Default string comparison
      else {
        const actualStr = actualValue === null ? "null" : String(actualValue);
        const expectedStr = String(expectedValue);
        status = actualStr === expectedStr ? "Pass" : "Fail";
      }
    } else {
      // For Dynamic attributes, just check that a value exists
      displayActualValue = String(actualValue);
      status = "Pass"; // Dynamic attributes pass as long as they're not missing or empty
    }

    // Look up proxy value for this attribute
    const proxyValue = flattenedProxy ? flattenedProxy[attributeName] : undefined;
    const displayProxyValue = proxyValue === undefined ? ""
      : proxyValue === null ? "null"
      : String(proxyValue);

    results.push({
      variableName: attributeName,
      expectedValue: expectedValue,
      actualValueMP: displayActualValue,
      actualValue: displayProxyValue,
      passFail: status,
      fieldType: attributeType,
      modified: false,
      isFlowAttribute: true // Add this to mark flow attributes
    });
  });

  // Check for extra attributes in actual data that aren't in expected
  const expectedAttributeNames = expectedAttributes.map(attr => attr.attribute_name);
  const alreadyAddedNames = new Set(results.map(r => r.variableName));

  Object.keys(flattenedActual).forEach(actualKey => {
    if (!expectedAttributeNames.includes(actualKey) && !alreadyAddedNames.has(actualKey)) {
      const actualValue = flattenedActual[actualKey];
      const displayValue = actualValue === null ? "null" : String(actualValue);

      // Look up proxy value for this extra attribute
      const proxyValue = flattenedProxy ? flattenedProxy[actualKey] : undefined;
      const displayProxyValue = proxyValue === undefined ? ""
        : proxyValue === null ? "null"
        : String(proxyValue);

      results.push({
        variableName: actualKey,
        expectedValue: "",
        actualValueMP: displayValue,
        actualValue: displayProxyValue,
        passFail: "Extra",
        fieldType: "N/A",
        modified: false,
        isFlowAttribute: false // Extra attributes are NOT flow attributes
      });
    }
  });

  return results;
};

const eventComparisonService = {
  compareEventWithFlow,
  getNestedValue
};

export default eventComparisonService;
